"use client";

import * as React from "react";
import type { PartialAiIconSet } from "@repo/utils/aiDisclaimerIcons";
import type { AiMarkerRegistry } from "@repo/utils/aiMarkerRegistry";
import {
  DEFAULT_AI_BADGE_POSITION,
  type AiBadgePosition,
} from "@repo/utils/aiBadgePosition";
import {
  DEFAULT_AI_BADGE_SIZE,
  type AiBadgeSize,
} from "@repo/utils/aiBadgeSize";

const AiDisclaimerIconContext = React.createContext<PartialAiIconSet | null>(
  null,
);

/**
 * Assets marked AI, keyed by base url.
 *
 * A separate context from the icons on purpose: the icons change when an editor rebinds
 * artwork, the registry changes on every bulk run, and a single context object would make
 * each of those re-render every consumer of the other.
 */
const AiMarkerRegistryContext = React.createContext<AiMarkerRegistry | null>(
  null,
);

/**
 * The site-wide corner, as configured in the CMS.
 *
 * Its own context for the same reason the registry has one: the default changes when an
 * editor moves it, the icons and the registry change on their own schedules, and one
 * object would re-render every consumer of the others.
 */
const AiBadgePositionContext = React.createContext<AiBadgePosition | null>(
  null,
);

const AiBadgeSizeContext = React.createContext<AiBadgeSize | null>(null);

export type AiDisclaimerProviderProps = React.PropsWithChildren<{
  icons?: PartialAiIconSet | null;
  /**
   * Assets marked AI in the CMS, so an image whose story field never copied the marker
   * still gets its badge. Storyblok copies an asset's alt into a field when the asset is
   * picked and then stops linking them, so a placement made before the asset was marked
   * carries no marker of its own.
   */
  markers?: AiMarkerRegistry | null;
  /**
   * The corner every badge uses unless the asset or the call site says otherwise.
   *
   * Absent falls back to `bottom-right`, the placement that predates the setting — a
   * space whose config story has no such field must keep rendering what it rendered.
   */
  position?: AiBadgePosition | null;
  /** The site-wide badge height in px; see `@repo/utils/aiBadgeSize`. */
  size?: AiBadgeSize | null;
}>;

/**
 * Supplies the AI badge icons for a subtree.
 *
 * The icons are bound in the CMS, so they arrive as URLs on a request rather than as
 * anything importable. A context is what keeps that out of the call sites: `AiDisclaimer`
 * renders in 17 places, and threading an icon prop through every one of them — and
 * through the components that merely pass images down — would make the eventual switch
 * from placeholder to bound artwork a change to all of them instead of to one provider.
 *
 * Mounting is optional. Without a provider the badge falls back to the bundled
 * placeholders, which is what makes a Storybook story or a test render work with no
 * setup.
 */
export function AiDisclaimerProvider({
  icons,
  markers,
  position,
  size,
  children,
}: AiDisclaimerProviderProps) {
  return (
    <AiDisclaimerIconContext.Provider value={icons ?? null}>
      <AiMarkerRegistryContext.Provider value={markers ?? null}>
        <AiBadgePositionContext.Provider value={position ?? null}>
          <AiBadgeSizeContext.Provider value={size ?? null}>
            {children}
          </AiBadgeSizeContext.Provider>
        </AiBadgePositionContext.Provider>
      </AiMarkerRegistryContext.Provider>
    </AiDisclaimerIconContext.Provider>
  );
}

export function useAiDisclaimerIcons(): PartialAiIconSet | null {
  return React.useContext(AiDisclaimerIconContext);
}

/**
 * The marked-asset registry for this subtree, or `null` where none was supplied.
 *
 * `null` is not an error: the badge then depends entirely on the alt it was given, which
 * is exactly the behaviour that predates the registry.
 */
export function useAiMarkerRegistry(): AiMarkerRegistry | null {
  return React.useContext(AiMarkerRegistryContext);
}

/**
 * The site-wide badge corner, or the built-in default where none is configured.
 *
 * Never `null` to the caller: a badge with no corner has no placement at all, and the
 * absolute positioning would drop it into the container's top-left over whatever is
 * there. Resolving the fallback here keeps that impossible at every call site.
 */
export function useAiBadgePosition(): AiBadgePosition {
  return React.useContext(AiBadgePositionContext) ?? DEFAULT_AI_BADGE_POSITION;
}

/** The site-wide badge height, or the default when no provider set one. */
export function useAiBadgeSize(): AiBadgeSize {
  return React.useContext(AiBadgeSizeContext) ?? DEFAULT_AI_BADGE_SIZE;
}
